import { useState, type ReactNode } from 'react';
import { Input } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AdminDesktopGpus, DesktopEnrolled, DesktopMachine } from './desktop-types';
import {
  groupCounts,
  groupMachines,
  groupName,
  matchingGroups,
  type MachineGroup,
} from './desktop-machines';
import { MachineTile, type Rename } from './MachineTile';
import { PolicyEditor } from './PolicyEditor';
import type { PolicyApi } from './use-policy-save';
import './gpu.css';
import './desktop.css';

export type { Rename } from './MachineTile';

export interface DesktopGpusBoardProps {
  view: AdminDesktopGpus;
  /** Staff may rename a machine; absent, no Rename shows. */
  onRename?: Rename | undefined;
  /** Staff may change a machine's cards, schedule and pause; absent, no Cards and schedule shows. */
  policyApi?: PolicyApi | undefined;
  /** The last refresh failed: why, in a sentence; the last picture stays up. */
  refreshFailed?: string | undefined;
}

type StateFilter = DesktopMachine['state'] | 'all';

/** The strip on top: how many machines are online, paused and offline, and the ones that never connected. Each is a filter. */
function Counts({
  view,
  groups,
  state,
  onState,
}: {
  view: AdminDesktopGpus;
  groups: MachineGroup[];
  state: StateFilter;
  onState: (s: StateFilter) => void;
}): ReactNode {
  const c = groupCounts(groups);
  const chip = (s: DesktopMachine['state'], n: number, words: string) => (
    <button
      type="button"
      className={`dg-count dg-count--${s}${n > 0 ? ' dg-count--some' : ''}`}
      aria-pressed={state === s}
      onClick={() => onState(state === s ? 'all' : s)}
    >
      <strong>{n}</strong> {words}
    </button>
  );
  return (
    <div className="dg-counts" role="group" aria-label="Machines by state">
      {chip('offline', c.offline, 'offline')}
      {chip('paused', c.paused, 'paused')}
      {chip('online', c.online, 'online')}
      {view.neverConnected.length > 0 ? (
        <span className="dg-count dg-count--never">
          <strong>{view.neverConnected.length}</strong> enrolled, never connected
        </span>
      ) : null}
    </div>
  );
}

function Never({ e }: { e: DesktopEnrolled }): ReactNode {
  const f = useFormat();
  return (
    <article className="dg-tile dg-tile--never">
      <header className="dg-tile__head">
        <strong className="dg-tile__name">{e.note}</strong>
        <span className="dg-state dg-state--never">Enrolled, never connected</span>
      </header>
      <p className="dg-tile__card">Its code works until {f.dateTime(e.expires)}</p>
    </article>
  );
}

/** A failed refresh, and a relay that did not answer: the picture is then a minute old. */
function Notices({ view, refreshFailed }: DesktopGpusBoardProps): ReactNode {
  return (
    <>
      {refreshFailed ? (
        <p className="pl-notice" role="status">
          Could not refresh: {refreshFailed} Showing the last picture.
        </p>
      ) : null}
      {!view.relayAnswering ? (
        <p className="dg-quiet" role="status">
          The relay did not answer: online and paused are as recorded a minute ago.
        </p>
      ) : null}
    </>
  );
}

/** The editor for the machine open, on the policy the screen reads now (so a change made elsewhere reaches it). */
function Editor({
  groups,
  open,
  api,
  onClose,
}: {
  groups: MachineGroup[];
  open: string | null;
  api: PolicyApi | undefined;
  onClose: () => void;
}): ReactNode {
  const g = groups.find((x) => x.id === open);
  if (!g?.policy || !api) return null;
  return (
    <PolicyEditor key={g.id} name={groupName(g)} state={g.policy} api={api} onClose={onClose} />
  );
}

/** Finding a machine, card or model once there are many. */
function Find({ text, onText }: { text: string; onText: (t: string) => void }): ReactNode {
  return (
    <Input
      aria-label="Find a machine"
      placeholder="Find a machine, card or model"
      value={text}
      onChange={(e) => onText(e.target.value)}
    />
  );
}

/**
 * Operations, Desktop GPUs: every desktop GPU machine of the last 30 days, a tile each with a row per card, offline
 * first and in red so a machine that went away can be brought back; what each card does now (its schedule, Paused, Not
 * used), the models on it, what works now, the build, the work per model today, over 7 and over 30 days, who changed its
 * cards and schedule last; Cards and schedule to change them; and the machines enrolled that never connected.
 */
export function DesktopGpusBoard({
  view,
  refreshFailed,
  onRename,
  policyApi,
}: DesktopGpusBoardProps): ReactNode {
  const [text, setText] = useState('');
  const [state, setState] = useState<StateFilter>('all');
  const [open, setOpen] = useState<string | null>(null);
  const groups = groupMachines(view);
  const shown = matchingGroups(groups, text, state);
  return (
    <div className="dg-board">
      <div className="dg-bar">
        <h1 className="dg-title">Desktop GPUs</h1>
        <Counts view={view} groups={groups} state={state} onState={setState} />
        {groups.length > 8 ? <Find text={text} onText={setText} /> : null}
      </div>
      <div className="dg-grid">
        {shown.map((g) => (
          <MachineTile
            key={g.id}
            g={g}
            onRename={onRename}
            onEdit={policyApi && g.policy ? () => setOpen(g.id) : undefined}
          />
        ))}
        {state === 'all' && !text
          ? view.neverConnected.map((e) => <Never key={e.note} e={e} />)
          : null}
      </div>
      {groups.length === 0 && view.neverConnected.length === 0 ? (
        <p className="dg-quiet">No desktop GPU has connected in the last 30 days.</p>
      ) : null}
      <Notices view={view} refreshFailed={refreshFailed} />
      <Editor groups={groups} open={open} api={policyApi} onClose={() => setOpen(null)} />
    </div>
  );
}
